import { useState } from 'react';
import { Link } from 'react-router-dom';

const serviceCards = [
  {
    name: 'iOS Development',
    description:
      'Native iPhone and iPad apps built for peak performance and seamless Apple ecosystem integration.',
    deliverables: [
      'Custom native development',
      'Store submission support',
      'Apple Watch/iPad extensions',
      'Performance optimization',
    ],
    icon: 'M7 4h10v16H7V4Zm5 13h.01M9 7h6',
  },
  {
    name: 'Android Development',
    description:
      'High-performance native Android apps built for the full diversity of the Android ecosystem.',
    deliverables: [
      'Custom native development',
      'Store submission support',
      'Material Design implementation',
      'Device compatibility testing',
    ],
    icon: 'M6 9h12v7a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V9Zm2-5 2 3m6-3-2 3M9 13h.01M15 13h.01',
  },
  {
    name: 'Cross-Platform Development',
    description:
      'One codebase, two platforms. Build faster without sacrificing native quality across app ecosystems.',
    deliverables: [
      'Cross-platform app builds',
      'Shared codebase architecture',
      'Native module integration',
      'Dual platform launch',
    ],
    icon: 'M4 7h16M7 4 4 7l3 3m10-6 3 3-3 3M8 17h8m-6-3 2 3 2-3',
  },
  {
    name: 'UI/UX Design',
    description:
      'Beautifully crafted interfaces that users love — from wireframes to polished, pixel-perfect prototypes.',
    deliverables: [
      'User research & personas',
      'Wireframes & user flows',
      'High-fidelity UI design',
      'Interactive Figma prototypes',
    ],
    icon: 'M5 5h6v6H5V5Zm8 0h6v6h-6V5ZM5 13h6v6H5v-6Zm8 0h6v6h-6v-6Z',
  },
  {
    name: 'App Strategy & Consulting',
    description:
      'Not sure where to start? Our strategists help you validate your idea, define your roadmap, and de-risk your build.',
    deliverables: [
      'Market & competitor analysis',
      'Feature prioritization (MoSCoW)',
      'MVP scoping',
      'Technical architecture review',
    ],
    icon: 'M12 3l8 5-8 5-8-5 8-5Zm-6 9 6 4 6-4M6 16l6 4 6-4',
  },
  {
    name: 'Maintenance & Support',
    description:
      'Your app is live — now keep it thriving. We handle updates, monitoring, bug fixes, and performance improvements.',
    deliverables: [
      'Ongoing bug fixes & patches',
      'OS update compatibility',
      'Performance monitoring',
      'Feature iteration sprints',
    ],
    icon: 'M12 4v3m0 10v3M4 12h3m10 0h3M6.5 6.5l2.1 2.1m6.8 6.8 2.1 2.1m0-11-2.1 2.1m-6.8 6.8-2.1 2.1M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z',
  },
];

const deepDiveRows = [
  {
    label: 'Discovery Phase',
    title: 'Strategy & Discovery',
    body: [
      'We start by understanding your business model, audience, constraints, and the moments that will make the product valuable.',
      'The result is a focused roadmap that de-risks the build before design or engineering begins.',
    ],
    points: ['Product goals and success metrics', 'User journeys and feature mapping', 'MVP scope and release priorities'],
    related: ['Strategy', 'MVP', 'Roadmapping'],
    visualLabel: 'Validated roadmap',
    image:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Team collaborating on product strategy in a meeting room',
  },
  {
    label: 'Design Phase',
    title: 'Design Excellence',
    body: [
      'Milestor designs interfaces with clarity, restraint, and a strong point of view, from low-fidelity flows to refined UI systems.',
      'You get interactive prototypes that make decisions tangible before development starts.',
    ],
    points: ['UX flows and wireframes', 'Design systems and components', 'Clickable Figma prototypes'],
    related: ['UI/UX', 'Figma', 'Design Systems'],
    visualLabel: 'Prototype ready',
    image:
      'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Designer reviewing mobile app interface layouts on screen',
  },
  {
    label: 'Build Phase',
    title: 'Engineering Power',
    body: [
      'Our engineers build reliable mobile products with clean architecture, rigorous testing, and weekly product demos.',
      'We choose the right stack for the job, whether native or cross-platform architecture best fits the roadmap.',
    ],
    points: ['Native and cross-platform builds', 'Backend and API integration', 'Automated QA and release pipelines'],
    related: ['iOS', 'Android', 'Cross-Platform'],
    visualLabel: 'Sprint velocity',
    image:
      'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Developer writing code on a laptop during an app build sprint',
  },
  {
    label: 'Growth Phase',
    title: 'Launch & Growth',
    body: [
      'Launch is only the start. We handle store submission, monitoring, analytics, and iteration plans after release.',
      'Your team leaves with documentation, confidence, and a roadmap for continuous improvement.',
    ],
    points: ['Store launch support', 'Analytics and monitoring setup', 'Post-launch support sprints'],
    related: ['Launch', 'Support', 'Analytics'],
    visualLabel: 'Live product',
    image:
      'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=900&q=80',
    imageAlt: 'Mobile phones displaying launched applications in use',
  },
];

const timelinePhases = [
  ['Discovery Call', 'Free 30-min call, understand your vision, assess technical needs', '01'],
  ['Proposal & Scoping', 'Detailed project scope, timeline estimate, fixed-price or sprint-based quote', '02'],
  ['Design Sprint', 'Wireframes, UI design, interactive prototype, client sign-off', '03'],
  ['Development', 'Agile sprints, regular demos, continuous client feedback loops', '04'],
  ['QA & Testing', 'Device testing, performance profiling, bug resolution, beta launch', '05'],
  ['Launch & Handover', 'App store submission, launch support, documentation, training', '06'],
];

const faqs = [
  [
    'How long does it take to build a mobile app?',
    'Most MVPs take 10-16 weeks, while larger multi-platform products can run across several sprint cycles depending on scope.',
  ],
  [
    'Do you build for both iOS and Android?',
    'Yes. We build native iOS and Android apps, plus cross-platform products when that approach fits the roadmap.',
  ],
  [
    'Can you work with our existing backend/API?',
    'Yes. We can integrate with existing APIs, audit backend readiness, and recommend improvements before mobile development begins.',
  ],
  [
    'What does the discovery phase involve?',
    'Discovery covers goals, audience, feature priorities, risks, technical architecture, and a launch-ready project scope.',
  ],
  [
    'Will I own the source code?',
    'Yes. Project deliverables include source code handover, documentation, and access to the agreed repositories and assets.',
  ],
  [
    'Do you offer ongoing maintenance after launch?',
    'Yes. We offer support plans for OS updates, bug fixes, analytics reviews, performance improvements, and feature iteration.',
  ],
  [
    'How do you handle project communication?',
    'We use weekly demos, sprint updates, shared project boards, and direct communication channels for quick decisions.',
  ],
  [
    'What information do I need to get started?',
    'Bring your idea, target users, goals, timeline, budget range, and any existing designs or technical documentation.',
  ],
];

function ServicesPage() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <main className="services-page">
      <section className="services-hero" aria-labelledby="services-hero-title">
        <div className="services-hero__inner">
          <p className="services-breadcrumb">
            <Link to="/">Home</Link> / Services
          </p>
          <p className="hero-chip">✦ Full-Spectrum Mobile Development</p>
          <h1 id="services-hero-title" className="services-hero__title">
            <span>Services Built for</span>
            <span>Builders Like You.</span>
          </h1>
          <p>
            From strategy and design to development and growth — we cover every stage of your app's
            journey.
          </p>
          <div className="services-hero__actions">
            <Link className="button button--primary" to="/contact">
              Start a Project
            </Link>
            <a className="button button--ghost" href="#services-overview">
              Explore Services ↓
            </a>
          </div>
        </div>
      </section>

      <section className="services-overview" id="services-overview" aria-labelledby="overview-title">
        <div className="services-section-heading services-section-heading--left reveal">
          <span>WHAT WE OFFER</span>
          <h2 id="overview-title">Everything Your App Needs to Succeed</h2>
          <p>Six core capabilities, one dedicated team.</p>
        </div>
        <div className="services-detail-grid">
          {serviceCards.map((service, index) => (
            <article
              className="service-detail-card reveal"
              key={service.name}
              style={{ '--reveal-delay': `${index * 80}ms` }}
            >
              <span className="service-detail-card__number">{String(index + 1).padStart(2, '0')}</span>
              <div className="service-detail-card__icon">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d={service.icon} />
                </svg>
              </div>
              <h3>{service.name}</h3>
              <p>{service.description}</p>
              <ul>
                {service.deliverables.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="services-deep-dive" aria-labelledby="deep-dive-title">
        <div className="services-section-heading services-section-heading--center reveal">
          <span>HOW WE WORK</span>
          <h2 id="deep-dive-title">A Deeper Look at How We Deliver</h2>
        </div>
        <div className="deep-dive-list">
          {deepDiveRows.map((row) => (
            <article className="deep-dive-row reveal" key={row.title}>
              <div className="deep-dive-visual">
                <img
                  className="deep-dive-visual__image"
                  src={row.image}
                  alt={row.imageAlt}
                  loading="lazy"
                  decoding="async"
                />
                <span>{row.visualLabel}</span>
              </div>
              <div className="deep-dive-copy">
                <span>{row.label}</span>
                <h3>{row.title}</h3>
                {row.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <ul>
                  {row.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
                <div className="related-tags" aria-label={`Related services for ${row.title}`}>
                  {row.related.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="project-timeline-section" aria-labelledby="project-timeline-title">
        <div className="services-section-heading services-section-heading--center reveal">
          <h2 id="project-timeline-title">How a Project Comes to Life</h2>
          <p>Our end-to-end engagement model — from first call to launch day.</p>
        </div>
        <div className="project-timeline">
          {timelinePhases.map(([title, description, number], index) => (
            <article
              className="project-timeline-item reveal"
              key={title}
              style={{ '--reveal-delay': `${index * 90}ms` }}
            >
              <span className="project-timeline-item__marker">{number}</span>
              <div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="faq-section" aria-labelledby="faq-title">
        <div className="services-section-heading services-section-heading--center reveal">
          <h2 id="faq-title">Frequently Asked Questions</h2>
          <p>Answers to the questions we hear most.</p>
        </div>
        <div className="faq-list reveal">
          {faqs.map(([question, answer], index) => {
            const isOpen = openFaq === index;

            return (
              <article className={`faq-item ${isOpen ? 'faq-item--open' : ''}`} key={question}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  onClick={() => setOpenFaq(isOpen ? -1 : index)}
                >
                  <span>{question}</span>
                  <b aria-hidden="true">{isOpen ? '×' : '+'}</b>
                </button>
                <div id={`faq-answer-${index}`} className="faq-item__answer">
                  <p>{answer}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="cta-banner" aria-labelledby="services-cta-title">
        <div className="cta-banner__inner reveal">
          <h2 id="services-cta-title">Have an App Idea? Let's Make It Real.</h2>
          <p>Book a free 30-minute discovery call and get a clear path to launch.</p>
          <div className="cta-banner__actions">
            <Link className="button button--light" to="/contact">
              Book a Free Call
            </Link>
            <Link className="button button--ghost" to="/">
              See Our Work <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ServicesPage;
